import * as THREE from 'three';
import { Vector3 } from 'three';

import { ElementObj } from '../../elements/element-obj';
import { InputHandler } from '../../input-handler';
import { publish, SubscriptionTopic } from '../../messaging/pubsub';
import {
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../../utilities/input-event/input-event-data-extractor';
import * as mathHelpers from '../../utilities/math-helpers';
import { Pick } from '../../utilities/pick';
import { ViewSettings } from '../../view-settings';
import { SkyviewPerspectiveCamera } from '../skyview-perspective-camera';
import { CameraControllerInterface, CameraControllerStateType } from '../types';
import { SceneManager } from './../../scene-manager';
import {
  isBoundingBoxWithinValidRange,
  shouldUpdateSceneOrigo,
  updateSceneOrigo,
} from './focus-utils';

/** The share of the distance to the point under the cursor to zoom per unit of wheel delta. */
const cursorZoomSpeed = 0.001;

/**
 * Orbits the camera around a pivot point in the middle of the view, and zooms towards the point
 * under the cursor. The other controllers of the perspective camera build on it.
 */
export class OrbitCameraController implements CameraControllerInterface {
  protected state: CameraControllerStateType;
  private focusedAsset: THREE.Object3D | undefined;

  private transitionEndTime: number = Date.now();
  constructor(protected camera: SkyviewPerspectiveCamera) {
    this.state = CameraControllerStateType.Idle;
  }

  /**
   *  Set the controller state based on the number of touches
   */
  onTouchStart(input: TouchInputData): boolean {
    // Not considered when evaluating movement
    // Just ensure that we are in idle state when we initiate movement
    this.state = CameraControllerStateType.Idle;
    return false;
  }

  /**
   *  Transform the camera based on the focus plance delta
   */
  onTouchMove(input: TouchInputData): boolean {
    switch (input.touches.length) {
      case 1: {
        const eventInformation = this.getFocalPointInformation(
          input.center!.position,
          new THREE.Vector2().addScaledVector(input.center!.delta, -1),
        );

        const planeDelta = eventInformation.focalPlaneDelta;

        if (!planeDelta) {
          break;
        }

        if (planeDelta.length() < 0.01) {
          planeDelta.normalize().multiplyScalar(0.01);
        }

        this.camera.move(planeDelta);
        this.state = CameraControllerStateType.Panning;
        this.unfocusAsset();
        break;
      }
      case 2: {
        // If we previously was not rotating, we need to identify the focus point
        if (this.state !== CameraControllerStateType.Rotating) {
          const pickedPosition = Pick.instance.closestPoint(this.camera, this.camera.getRay(0, 0), {
            pickHeight: Pick.instance.groundHeight(),
          });
          if (pickedPosition) {
            this.recalculateFocusPosition(pickedPosition);
          }
        }
        this.camera.setDisplayFocusPoint(true);
        this.camera.rotate(-input.center!.delta.x * 3, input.center!.delta.y * 3);

        this.state = CameraControllerStateType.Rotating;

        // Do we have a distance delta, zoom accordingly
        if (input.touchLength) {
          this.camera.zooming(-input.touchLength.delta * 1000);
        }
        break;
      }
      default:
        // More than 2 are not supported, and undefined behaviour. Do nothing
        this.state = CameraControllerStateType.Idle;
    }

    return (
      this.state === CameraControllerStateType.Rotating ||
      this.state === CameraControllerStateType.Panning
    );
  }

  /**
   *  Reset the camera control state
   */
  onTouchEnd(input: TouchInputData): boolean {
    this.state = CameraControllerStateType.Idle;
    this.camera.setDisplayFocusPoint(false);

    this.onTransitionStop();

    return false;
  }

  /**
   *  Set the controller state based on the mouse button pressed
   */
  onMouseDown(input: MouseInputData) {
    this.camera.setDisplayFocusPoint(true);
    if (
      input.button.type === MouseButtonType.LeftButton ||
      input.button.type === MouseButtonType.MiddleButton
    ) {
      this.state = CameraControllerStateType.Panning;
    } else if (input.button.type === MouseButtonType.RightButton) {
      this.state = CameraControllerStateType.Rotating;
      this.refreshFocusPoint();
    }

    return false;
  }

  refreshFocusPoint() {
    if (!this.focusedAsset) {
      const raycaster = this.camera.getRay(0, 0);

      /**
       * Use a large treshold to make navigation easier.
       */
      raycaster.params.Points = { threshold: 10 };
      raycaster.params.Mesh = { threshold: 10 };
      raycaster.params.Line = { threshold: 10 };

      // On the ground, e.g. the orthophoto, where no timeline data is in the middle of the view
      const pickedPosition = Pick.instance.closestPoint(this.camera, raycaster, {
        pickHeight: Pick.instance.groundHeight(),
      });

      if (pickedPosition) {
        this.recalculateFocusPosition(
          mathHelpers.findClosestPointOnRay(
            raycaster.ray.origin,
            raycaster.ray.direction,
            pickedPosition,
          ),
        );
      }
    }
  }

  /**
   *  Transform the camera based on the focus plance delta
   */
  onMouseMove(input: MouseInputData) {
    const eventInformation = this.getFocalPointInformation(
      input.pointer.position,
      input.pointer.delta,
    );

    if (
      InputHandler.instance.ctrlIsDown &&
      (this.state === CameraControllerStateType.Panning ||
        this.state === CameraControllerStateType.Rotating)
    ) {
      this.camera.rotatePivot(input.pointer.delta.x * 2.0, -input.pointer.delta.y * 2.0);
    } else if (
      eventInformation.focalPlaneDelta &&
      this.state === CameraControllerStateType.Panning
    ) {
      const planeDelta = eventInformation.focalPlaneDelta;
      if (planeDelta.length() < 0.01) {
        planeDelta.normalize().multiplyScalar(0.01);
      }
      this.camera.move(planeDelta);
      this.unfocusAsset();
    } else if (this.state === CameraControllerStateType.Rotating) {
      this.camera.rotate(input.pointer.delta.x * 2.0, -input.pointer.delta.y * 2.0, undefined);
    }

    return (
      (eventInformation.focalPlaneDelta && this.state === CameraControllerStateType.Panning) ||
      this.state === CameraControllerStateType.Rotating
    );
  }

  /**
   *  Reset the camera control state
   */
  onMouseUp(input: MouseInputData) {
    if (this.state === CameraControllerStateType.Panning) {
      this.refreshFocusPoint();
    }

    this.state = CameraControllerStateType.Idle;

    this.camera.setDisplayFocusPoint(false);

    this.onTransitionStop();

    return false;
  }

  /**
   *  Zoom towards the point under the cursor, or towards the pivot where nothing is under it, e.g.
   *  the sky
   */
  onMouseWheel(input: MouseInputData) {
    if (this.zoomTowardsCursor(input)) {
      return true;
    }

    const unusedZoom = this.camera.zooming(-input.wheel.delta);

    if (!this.focusedAsset) {
      this.camera.move(
        this.camera
          .getWorldDirection(new THREE.Vector3())
          .clone()
          .normalize()
          .multiplyScalar(-unusedZoom),
      );
    }

    this.onTransitionStop();

    return true;
  }

  /**
   * Moves the camera towards the point under the cursor, or away from it, by a share of the
   * distance to it, as the wheel zooms to the pivot. The pivot then lies straight ahead, as deep as
   * that point. Returns false where nothing is under the cursor, not even the ground.
   */
  private zoomTowardsCursor(input: MouseInputData) {
    const raycaster = this.camera.getRay(input.pointer.position.x, input.pointer.position.y);
    const point = Pick.instance.closestPoint(this.camera, raycaster, {
      pickHeight: Pick.instance.groundHeight(),
    });
    if (!point) {
      return false;
    }

    const cameraPosition = this.camera.getWorldPosition(new THREE.Vector3());
    // Never as far as the point itself
    const share = Math.min(input.wheel.delta * cursorZoomSpeed, 0.9);
    const offset = raycaster.ray.direction
      .clone()
      .multiplyScalar(cameraPosition.distanceTo(point) * share);
    this.camera.move(offset);
    this.unfocusAsset();

    const newCameraPosition = cameraPosition.add(offset);
    const forward = this.camera.getWorldDirection(new THREE.Vector3());
    const depth = point.clone().sub(newCameraPosition).dot(forward);
    if (depth > 0) {
      this.recalculateFocusPosition(newCameraPosition.addScaledVector(forward, depth));
    }

    this.onTransitionStop();
    return true;
  }

  unfocusAsset(): void {
    if (this.focusedAsset) {
      publish(SubscriptionTopic.SkyviewAssetFocusChanged, { assetUuid: undefined });
    }
    this.focusedAsset = undefined;
  }

  /**
   *  Focus the camera on an asset.
   */
  focusOnAsset(asset: ElementObj | undefined): void {
    // Do not focus on invisible assets.
    if (asset?.visible !== true) {
      return;
    }

    publish(SubscriptionTopic.SkyviewAssetFocusChanged, { assetUuid: asset.uuid });

    const bb = asset.generateBoundingBox(true);

    if (!isBoundingBoxWithinValidRange(bb)) {
      throw new Error(
        'Error focusing on asset. The asset is not located within the valid range of the projects coordinate system.',
      );
    }

    const hasToUpdateSceneOrigo = shouldUpdateSceneOrigo(bb, ViewSettings.instance.isCadViewer);
    if (hasToUpdateSceneOrigo) {
      updateSceneOrigo(bb.min.clone());
    }

    // Call after timeout so the asset has time to recalculate its position with pubsub event.
    setTimeout(
      () => {
        this.internalFocusOnBox(asset.generateBoundingBox(true));
        this.focusedAsset = asset;
      },
      hasToUpdateSceneOrigo ? 200 : 0,
    );
  }

  /**
   *  Return the asset currently in focus
   */
  getFocusedAsset() {
    return this.focusedAsset;
  }

  /**
   *  Focus around a bounding box
   */
  focusOnBox(bb: THREE.Box3) {
    this.internalFocusOnBox(bb);

    this.unfocusAsset();
  }

  /**
   *  Fetches the geodata bounderies and focuses on it
   */
  focusOnScene() {
    const activeScene = SceneManager.instance.getActiveScene();
    if (!activeScene) {
      return;
    }
    const bb = activeScene.getGeodataBounderies();
    if (!bb) {
      console.log(`There is not bb defined`);
      return;
    }

    try {
      const centerPoint = bb.getCenter(new THREE.Vector3());
      // Multiply with 0.65 to get a good overview distance to the geodata
      const distance = bb.getSize(new Vector3()).length() * 0.65;

      this.camera.rotate(-Math.PI / 4, Math.PI / 4);
      this.camera.setFocusPointPosition(centerPoint);
      this.camera.setZoom(distance);
    } catch (err) {
      console.log(`Error when focusing on scene geodata: ${JSON.stringify(err, null, 2)}`);
    }
  }

  /**
   *  Returns true if the camera controller has been in transition within the last half minute
   */
  isTransitioning() {
    return (
      this.state !== CameraControllerStateType.Idle || Date.now() - this.transitionEndTime < 500
    );
  }

  /**
   *  Re-position the focus point to a new position, without moving the camera
   */
  protected recalculateFocusPosition(newPosition: THREE.Vector3) {
    const distanceToNewPosition = newPosition
      .clone()
      .sub(this.camera.getWorldPosition(new THREE.Vector3()))
      .length();

    this.camera.setFocusPointPosition(newPosition);
    this.camera.setZoom(distanceToNewPosition);
  }

  /**
   *  Generate a plane at the focus point and the calculate the intersection point
   */
  private getFocalPlaneIntersection(x: number, y: number) {
    if (!this.camera) {
      return;
    }
    const focalPoint = this.camera.getFocusPoint();

    const focalPointWorldPosition = new THREE.Vector3();
    const cameraWorldPosition = new THREE.Vector3();
    focalPoint.getWorldPosition(focalPointWorldPosition);
    this.camera.getWorldPosition(cameraWorldPosition);
    return mathHelpers.rayPlaneIntersection(
      this.camera.getRay(x, y),
      focalPointWorldPosition.clone().sub(cameraWorldPosition),
      focalPointWorldPosition,
    );
  }

  /**
   *  Calculate the new intersection point and delta of the focus plane
   */
  private getFocalPointInformation(mousePosition: THREE.Vector2, mouseDelta: THREE.Vector2) {
    const prevMousePos = mousePosition.clone().add(mouseDelta);

    const currentFocalPlaneIntersection = this.getFocalPlaneIntersection(
      mousePosition.x,
      mousePosition.y,
    )!;
    const lastfocalPlaneIntersection = this.getFocalPlaneIntersection(
      prevMousePos.x,
      prevMousePos.y,
    );

    return {
      focalPlaneIntersection: currentFocalPlaneIntersection,
      focalPlaneDelta:
        lastfocalPlaneIntersection &&
        lastfocalPlaneIntersection.clone().sub(currentFocalPlaneIntersection),
    };
  }

  protected onTransitionStop() {
    this.transitionEndTime = Date.now();
  }

  /**
   *  Focuses the camera viewport on a Box3.
   *  Handles transition state and publishes the camera moved event.
   */
  private internalFocusOnBox(bb: THREE.Box3) {
    this.camera.focusOnBox(bb, undefined, () => {
      this.state = CameraControllerStateType.Idle;
      this.onTransitionStop();

      publish(SubscriptionTopic.OnCameraMoved, { camera: this.camera });
    });

    this.state = CameraControllerStateType.Tweening;
  }
}
